/**
 * Exchange-rate fetcher with 1-hour in-memory + localStorage cache.
 *
 * Uses the free ExchangeRate-API (v6) endpoint which requires no key for
 * the `/latest/<BASE>` path. Rates are cached to avoid hammering the API
 * and to survive page refreshes within the cache window.
 *
 * @see https://www.exchangerate-api.com/docs/free
 */

const API_BASE = "/api/exchange-rates";
const CACHE_KEY = "dwellfinder_exchange_rates";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export interface RateCache {
  base: string;
  rates: Record<string, number>;
  fetchedAt: number; // epoch ms
}

/** In-memory singleton so we never fetch twice in the same session. */
let memoryCache: RateCache | null = null;

/** Read from localStorage (survives refresh). */
function readStorageCache(): RateCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as RateCache;
  } catch {
    return null;
  }
}

/** Persist to localStorage. */
function writeStorageCache(cache: RateCache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota exceeded – not critical */
  }
}

function isFresh(cache: RateCache | null, base: string): cache is RateCache {
  if (!cache) return false;
  if (cache.base !== base) return false;
  return Date.now() - cache.fetchedAt < CACHE_TTL_MS;
}

/**
 * Fetch exchange rates for the given base currency.
 * Returns a map of currency code → rate (e.g. `{ USD: 1, GHS: 15.4, ... }`).
 */
export async function getExchangeRates(
  base: string = "USD",
): Promise<Record<string, number>> {
  const normBase = base.toUpperCase();

  // 1. In-memory cache
  if (isFresh(memoryCache, normBase)) return memoryCache.rates;

  // 2. localStorage cache
  const stored = readStorageCache();
  if (isFresh(stored, normBase)) {
    memoryCache = stored;
    return stored.rates;
  }

  // 3. Fetch fresh rates
  const res = await fetch(`${API_BASE}?base=${normBase}`);
  if (!res.ok) throw new Error(`Exchange rate fetch failed: ${res.status}`);
  const json = await res.json();

  if (json.result !== "success" || !json.rates) {
    throw new Error("Unexpected exchange rate API response");
  }

  const cache: RateCache = {
    base: normBase,
    rates: json.rates as Record<string, number>,
    fetchedAt: Date.now(),
  };

  memoryCache = cache;
  writeStorageCache(cache);
  return cache.rates;
}

/**
 * Convert an amount from one currency to another.
 *
 * @returns The converted amount, or `null` if rates are unavailable.
 */
export function convertAmount(
  amount: number,
  from: string,
  to: string,
  rates: Record<string, number>,
): number | null {
  const fromUpper = from.toUpperCase();
  const toUpper = to.toUpperCase();
  if (fromUpper === toUpper) return amount;

  const fromRate = rates[fromUpper];
  const toRate = rates[toUpper];
  if (!fromRate || !toRate) return null;

  // Convert via base: amount / fromRate * toRate
  return (amount / fromRate) * toRate;
}

/** Attribution string for the exchange rate provider. */
export const RATE_PROVIDER_LABEL = "ExchangeRate-API";
